/**
 * Onboarding API
 *
 * RTK Query endpoints for user onboarding profiles and personalized assessment paths.
 */

import { api } from '../api';

// ============================================
// TYPES
// ============================================

export type PrimaryIntent =
  | 'improve_speaking'
  | 'get_hired'
  | 'prove_expertise'
  | 'close_more_deals'
  | 'better_public_speaking';

export type ExperienceLevel = 'beginner' | 'intermediate' | 'advanced';

export type PathStepStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

export interface OnboardingProfile {
  id: string;
  userId: string;
  primaryIntent: PrimaryIntent;
  currentRole: string | null;
  targetRole: string | null;
  experienceLevel: ExperienceLevel | null;
  preferredDomains: string[];
  focusCategories: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PathStepObjective {
  id: string;
  key: string;
  objectiveText: string;
  difficulty: string;
  category?: {
    id: string;
    key: string;
    name: string;
    domain?: {
      id: string;
      key: string;
      name: string;
    };
  };
}

export interface PathStep {
  id: string;
  orderIndex: number;
  status: PathStepStatus;
  completedAt: string | null;
  objective: PathStepObjective;
}

export interface PathProgress {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
}

export interface AssessmentPath {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  steps: PathStep[];
  progress: PathProgress;
}

export interface SaveProfileRequest {
  primaryIntent: PrimaryIntent;
  currentRole?: string | null;
  targetRole?: string | null;
  experienceLevel?: ExperienceLevel | null;
  preferredDomains?: string[];
  focusCategories?: string[];
  /** Explicit, confirmed retake: only then may the server replace an active path. */
  retake?: boolean;
}

export interface SaveProfileResponse {
  profile: OnboardingProfile;
  path: AssessmentPath | null;
}

export interface GetProfileResponse {
  profile: OnboardingProfile | null;
  hasCompletedOnboarding: boolean;
  /**
   * The active assessment path, present because this client requests
   * GET /onboarding/me?include=path (same value as GET /onboarding/me/path's
   * `path`). Absent only if a server predates the include — see
   * hooks/useOnboardingPath, which then falls back to the path endpoint.
   */
  path?: AssessmentPath | null;
  /**
   * The first "Get Started" on the onboarding Welcome for this user in the
   * token's workspace (POST /onboarding/me/start), or null. Meaningful only
   * when `startedAtKnown` is true; otherwise (a server that predates the
   * field, migration 20261012 not applied yet, no workspace) the client keeps
   * its local fallback (utils/onboardingWelcome).
   */
  startedAt?: string | null;
  startedAtKnown?: boolean;
}

export interface StartOnboardingResponse {
  startedAt: string;
  /** false when an earlier Get Started was already recorded (its time is kept). */
  created: boolean;
}

export interface GetPathResponse {
  path: AssessmentPath | null;
}

export interface GetNextStepResponse {
  nextStep: PathStep | null;
  pathComplete: boolean;
}

export interface StartAssessmentRequest {
  stepId: string;
  userContext?: {
    userName?: string;
    userRole?: string;
    userExperience?: string;
  };
}

export interface StartAssessmentResponse {
  session: {
    id: string;
    objectiveId: string;
    domainKey: string | null;
    categoryKey: string | null;
    difficulty: string;
    status: string;
    turnCount: number;
    templateId: string | null;
    pathStepId: string;
  };
  openingQuestion: string;
  expertPersona: {
    name: string;
    title: string;
    bio: string;
  } | null;
  maxTurns: number;
}

export interface StepActionResponse {
  step: {
    id: string;
    status: PathStepStatus;
    completedAt?: string | null;
  };
  path: AssessmentPath | null;
}

// API Response wrapper
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================
// API ENDPOINTS
// ============================================

export const onboardingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ----------------------------------------
    // PROFILE MANAGEMENT
    // ----------------------------------------

    // Get user's onboarding profile, with the active path included.
    // One cache entry serves the route guard (profile) and the dashboard
    // (path), so a fresh /dashboard makes one onboarding request, not two.
    // It provides 'SkillProgress' because it carries the path: every path
    // mutation below invalidates that tag.
    getOnboardingProfile: builder.query<GetProfileResponse, void>({
      query: () => '/onboarding/me?include=path',
      transformResponse: (response: ApiResponse<GetProfileResponse>) => response.data,
      providesTags: ['User', 'SkillProgress'],
    }),

    // Save/update onboarding profile and generate path
    saveOnboardingProfile: builder.mutation<SaveProfileResponse, SaveProfileRequest>({
      query: (body) => ({
        url: '/onboarding/me',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<SaveProfileResponse>) => response.data,
      invalidatesTags: ['User', 'SkillProgress'],
    }),

    // Record "Get Started" on the Welcome (token user + token workspace;
    // idempotent, takes no input). No tag invalidation: the caller writes the
    // answer into the cached GET /onboarding/me (OnboardingWizardPage).
    startOnboarding: builder.mutation<StartOnboardingResponse, void>({
      query: () => ({
        url: '/onboarding/me/start',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<StartOnboardingResponse>) => response.data,
    }),

    // ----------------------------------------
    // PATH MANAGEMENT
    // ----------------------------------------

    // Get user's active assessment path
    getAssessmentPath: builder.query<AssessmentPath | null, void>({
      query: () => '/onboarding/me/path',
      transformResponse: (response: ApiResponse<GetPathResponse>) => response.data.path,
      providesTags: ['SkillProgress'],
    }),

    // Get next step in path
    getNextStep: builder.query<GetNextStepResponse, void>({
      query: () => '/onboarding/me/path/next-step',
      transformResponse: (response: ApiResponse<GetNextStepResponse>) => response.data,
      providesTags: ['SkillProgress'],
    }),
  }),
  overrideExisting: false,
});

// ============================================
// DERIVED READS
// ============================================

/**
 * The next step of a path, derived client-side from GET /onboarding/me/path.
 *
 * Mirrors OnboardingService.getNextStep exactly — the first in_progress step,
 * else the first pending step, in orderIndex order; no path or no such step is
 * `{ nextStep: null, pathComplete: true }` — so a screen that already holds the
 * path does not need a second request to /onboarding/me/path/next-step. That
 * endpoint and useGetNextStepQuery stay for any other caller.
 */
export function deriveNextStep(path: AssessmentPath | null): GetNextStepResponse {
  const steps = [...(path?.steps ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
  const nextStep =
    steps.find((s) => s.status === 'in_progress') ??
    steps.find((s) => s.status === 'pending') ??
    null;
  return { nextStep, pathComplete: nextStep === null };
}

// ============================================
// EXPORTED HOOKS
// ============================================

export const {
  // Profile
  useGetOnboardingProfileQuery,
  useSaveOnboardingProfileMutation,
  useStartOnboardingMutation,
  // Path
  useGetAssessmentPathQuery,
  useGetNextStepQuery,
} = onboardingApi;
